import { useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider, LinearProgress, Link, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ProvenanceBadge from './ProvenanceBadge';
import PrimaryLicenseChip from './PrimaryLicenseChip';
import ComplianceCelebration, { EASE_OUT_QUART, SubGoalFlash, chipPop } from './ComplianceCelebration';
import { ETHICS_SUB_ID, isSubjectRequirementMet } from './celebration';
import { hideHoursBasedEthics } from './ethicsDisplay';
import ManualRequirementTile from './ManualRequirementTile';
import { getUrgency } from './urgency';
import { getLicenseFulfillmentAtRisk } from './atRisk';
import { formatUtcDate } from '../../utils/dateTime';
import licenseComplianceService from '../../services/licenseComplianceService';
import { getApiErrorInfo } from '../../services/api';
import type {
  CreditLimitFulfillmentDto,
  LicenseFulfillmentDto,
  SubjectRequirementFulfillmentDto,
  YearBucketDto,
} from '../../services/licenseComplianceService';
import { formatCreditLimitBasis } from '../../utils/creditLimitBasis';

interface LicenseFulfillmentPanelProps {
  license: LicenseFulfillmentDto;
  /**
   * Display name of the primary license, resolved by the parent (the panel only
   * sees one license). For derived entries it's the mirrored source (from
   * `license.reciprocitySourceLicenseId`); for eligible-but-not-elected entries
   * it names the primary in the discovery hint.
   */
  reciprocitySourceName?: string | null;
  /**
   * Enables check-off and evidence linking on manual requirement tiles.
   * Only valid for the current user's own view (calls /users/me endpoints);
   * the admin drawer renders read-only by omitting it.
   */
  interactive?: boolean;
  /** Called after a manual requirement changes so the parent can refresh fulfillment. */
  onChanged?: () => void | Promise<void>;
  /**
   * True when this license's compliance goal was first observed met on this
   * visit. Triggers the one-time goal-reached celebration (confetti + message).
   */
  justAchieved?: boolean;
  /**
   * Sub-goal ids (requirement ids, or ETHICS_SUB_ID) first observed met on this
   * visit while the license is still in progress. Each fires the smaller
   * celebration (tile flash + chip pop) on its tile.
   */
  achievedSubIds?: ReadonlySet<string>;
}

function StatusChip({ compliant, pop = false }: { compliant: boolean; pop?: boolean }) {
  // One-Green Rule: in progress is blue (status.onPace), so green appears once
  // on compliance surfaces and always means "done". The check reinforces it.
  const key = compliant ? 'completed' : 'onPace';
  return (
    <Chip
      label={compliant ? 'Compliant' : 'In progress'}
      size="small"
      icon={compliant ? <CheckCircleRoundedIcon /> : undefined}
      sx={{
        bgcolor: `status.${key}.bg`,
        color: `status.${key}.fg`,
        fontWeight: compliant ? 600 : undefined,
        '& .MuiChip-icon': { fontSize: 15, color: 'inherit' },
        animation: pop ? `${chipPop} 0.45s ${EASE_OUT_QUART} both` : undefined,
      }}
    />
  );
}

interface RequirementTileProps {
  name: string;
  earned: number;
  required: number;
  done: boolean;
  /** Status token + label for the chip. Defaults to Complete/In Progress from `done`. */
  chip?: { key: 'completed' | 'inProgress' | 'attention'; label: string };
  /** Secondary line under the name (e.g. what counts toward the requirement). */
  detail?: string;
  /** When set, renders per-year bucket rows instead of a progress bar. */
  buckets?: YearBucketDto[] | null;
  /** Fires the small sub-goal celebration (tile flash + chip pop). Caller gates reduced motion. */
  justMet?: boolean;
}

/** One requirement tile in the subject breakdown (ethics or a subject requirement). */
function RequirementTile({ name, earned, required, done, chip, detail, buckets, justMet = false }: RequirementTileProps) {
  // required <= 0 means the state has no such requirement (or data is missing) —
  // implying a satisfied requirement there would be false.
  const notRequired = required <= 0;
  const pct = required > 0 ? Math.min(100, (earned / required) * 100) : 0;
  return (
    <Box sx={{ position: 'relative' }}>
      {justMet && <SubGoalFlash />}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {name}
          </Typography>
          {/* Exception-only signaling: in-progress is silent (the numbers and
              bar already say it), done is a check glyph, and only exceptional
              states — Not required, Attention required — get a chip. */}
          {notRequired ? (
            <Chip
              label="Not required"
              size="small"
              sx={{ bgcolor: 'status.enrolled.bg', color: 'status.enrolled.fg' }}
            />
          ) : chip ? (
            <Chip
              label={chip.label}
              size="small"
              sx={{ bgcolor: `status.${chip.key}.bg`, color: `status.${chip.key}.fg` }}
            />
          ) : done ? (
            <CheckCircleRoundedIcon
              titleAccess="Complete"
              sx={{
                fontSize: 16,
                color: 'status.completed.fg',
                animation: justMet ? `${chipPop} 0.45s ${EASE_OUT_QUART} both 0.2s` : undefined,
              }}
            />
          ) : null}
        </Box>
        {!notRequired && (
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
            {earned.toFixed(1)} / {required}
          </Typography>
        )}
      </Box>
      {detail && (
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
          {detail}
        </Typography>
      )}
      {buckets && buckets.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {buckets.map((bucket) => {
            const bucketKey = bucket.isMet
              ? 'completed'
              : bucket.isElapsed
                ? 'attention'
                : 'onPace';
            return (
              <Box
                key={bucket.index}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: `status.${bucketKey}.dot`,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                    Year {bucket.index + 1} · {formatUtcDate(bucket.start)} – {formatUtcDate(bucket.end)}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontVariantNumeric: 'tabular-nums', color: `status.${bucketKey}.fg`, flexShrink: 0 }}
                >
                  {bucket.earnedHours.toFixed(1)} / {bucket.requiredHours}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : notRequired ? null : (
        // Single-hue rule: the bar shows amount only; done state lives in the
        // check glyph beside the name.
        <LinearProgress
          variant="determinate"
          value={pct}
          // Explicit track: MUI's default lightened-primary track sits under
          // 3:1 against the bar; tint.primarySoft clears the non-text minimum.
          sx={{ height: 4, bgcolor: 'tint.primarySoft' }}
        />
      )}
    </Box>
  );
}

function limitScopeText(scope: CreditLimitFulfillmentDto['scope']): string {
  return scope === 'per_year' ? 'each year' : 'entire period';
}

/**
 * One credit limit meter: how much of a delivery-method cap has been used.
 * Exceeding is informational, never a violation — the excess simply doesn't
 * count toward the total, so exceeded state uses the neutral modified tint,
 * not an alarm color. The meter itself is neutral grey, not brand green:
 * green bars mean progress toward a goal, and filling a cap is consumption
 * of an allowance — the same hue would make "limit reached" scan as an
 * achievement.
 */
function CreditLimitTile({ limit }: { limit: CreditLimitFulfillmentDto }) {
  const hasCap = limit.effectiveCapHours != null && limit.effectiveCapHours > 0;
  const pct = hasCap ? Math.min(100, (limit.usedHours / limit.effectiveCapHours!) * 100) : 0;
  const buckets = limit.scope === 'per_year' ? limit.yearBuckets : null;
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {limit.name}
          </Typography>
          {limit.isExceeded && (
            <Chip
              label="Limit reached"
              size="small"
              sx={{ bgcolor: 'status.modified.bg', color: 'status.modified.fg' }}
            />
          )}
        </Box>
        {hasCap && !buckets && (
          <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', color: 'text.secondary' }}>
            {limit.usedHours.toFixed(1)} / {limit.effectiveCapHours}
          </Typography>
        )}
      </Box>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
        {formatCreditLimitBasis(limit)} · max applies to {limitScopeText(limit.scope)}
      </Typography>
      {buckets && buckets.length > 0 ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          {buckets.map((bucket) => {
            // status.modified has no dot token; its fg (dark amber) clears the
            // 3:1 non-text minimum, so it doubles as the exceeded dot color.
            // Within-cap buckets stay neutral (allowance, not progress).
            const dotColor = bucket.isExceeded ? 'status.modified.fg' : 'status.enrolled.dot';
            const fgColor = bucket.isExceeded ? 'status.modified.fg' : 'text.secondary';
            return (
              <Box
                key={bucket.index}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
                  <Box
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: '50%',
                      bgcolor: dotColor,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                    Year {bucket.index + 1} · {formatUtcDate(bucket.start)} – {formatUtcDate(bucket.end)}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{ fontVariantNumeric: 'tabular-nums', color: fgColor, flexShrink: 0 }}
                >
                  {bucket.usedHours.toFixed(1)}
                  {bucket.effectiveCapHours != null ? ` / ${bucket.effectiveCapHours}` : ''}
                </Typography>
              </Box>
            );
          })}
        </Box>
      ) : hasCap ? (
        // Single-hue rule: the bar shows usage toward the cap; the exceeded
        // state lives in the chip and the note below. color="inherit" makes
        // the fill use the sx color (neutral — see the component comment);
        // enrolled.fg on the neutralMuted track clears the 3:1 minimum.
        <LinearProgress
          variant="determinate"
          color="inherit"
          value={pct}
          sx={{ height: 4, color: 'status.enrolled.fg', bgcolor: 'tint.neutralMuted' }}
        />
      ) : (
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          {limit.usedHours.toFixed(1)} hrs used. This limit can't be enforced until your total
          requirement is set.
        </Typography>
      )}
      {limit.isExceeded && (
        <Typography variant="caption" sx={{ color: 'status.modified.fg', display: 'block', mt: 1 }}>
          {limit.excessHours.toFixed(1)} hrs over the limit do not count toward your total.
          They still appear in your CPE history.
        </Typography>
      )}
    </Box>
  );
}

function subjectDetail(req: SubjectRequirementFulfillmentDto): string {
  const target =
    req.targetKind === 'total'
      ? 'Any subject'
      : req.targetKind === 'technical'
        ? 'Any technical subject'
        : req.targetKind === 'non_technical'
          ? 'Any non-technical subject'
          : req.categoryNames?.length
            ? req.categoryNames.join(', ')
            : 'Selected categories';
  return req.scope === 'per_year' ? `${target} · required each year` : target;
}

/** Per-license CPE fulfillment card: header with status, hero total-hours bar, ethics breakdown. */
export default function LicenseFulfillmentPanel({
  license,
  reciprocitySourceName,
  interactive = false,
  onChanged,
  justAchieved = false,
  achievedSubIds,
}: LicenseFulfillmentPanelProps) {
  const reducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  // The sub-goal moment is purely decorative (the chip carries the state), so
  // it's skipped entirely under prefers-reduced-motion.
  const subJustMet = (id: string) => !reducedMotion && (achievedSubIds?.has(id) ?? false);
  // Derived entry: compliance mirrors the primary license (home-state
  // reciprocity), so the hero bar / subject breakdown / credit limits would
  // all be vacuous — they're replaced by the "satisfied via" summary. A
  // gate-failed entry fell back to standard evaluation server-side and renders
  // normally with a notice.
  const derived = license.complianceMode === 'home_state_reciprocity' && !license.reciprocityGateFailed;
  const sourceLabel = reciprocitySourceName || 'home state';
  const { daysLeft, attentionRequired, periodEnded } = getUrgency(license.periodEnd, license.isCompliant);
  const { atRisk } = getLicenseFulfillmentAtRisk(license);
  // Countable hours (credit limits applied + carry-over folded in server-side)
  // drive the hero — excess over a cap doesn't bring the license closer to its
  // goal. Falls back to raw for older payloads.
  const countableTotal = license.countableTotalHours ?? license.earnedTotalHours;
  // Carry-over is inside countableTotal, so back it out before deriving the
  // capped excess — otherwise applied carry-over masks "over a credit limit".
  const appliedCarryover = license.carryoverHours ?? 0;
  const cappedExcess = Math.max(0, license.earnedTotalHours - (countableTotal - appliedCarryover));
  const creditLimits = license.creditLimits ?? [];

  // This panel is the only carry-over surface. Apply and Exclude are strict
  // opposites: the banner offers Apply, and Exclude only exists as its undo in
  // the applied caption (writing the explicit 0 — "user said no" — which
  // suppresses the banner; the excluded caption then offers re-apply). Both
  // send the current targets verbatim (confirm:false) so the server's
  // prefill-match and status machine stay untouched.
  const [carryoverAction, setCarryoverAction] = useState<'apply' | 'exclude' | null>(null);
  const [carryoverApplyError, setCarryoverApplyError] = useState<string | null>(null);
  // Announced through the persistent hidden status region below — the banner
  // disappears on success, so without this the outcome would be silent to
  // screen readers (errors already announce via the Alert).
  const [carryoverAnnouncement, setCarryoverAnnouncement] = useState('');
  const suggestedCarryover = license.suggestedCarryoverHours ?? 0;
  // No banner once compliant: a suggestion can't bring a met goal closer, and
  // it would sit next to the celebration line as pure noise.
  const showCarryoverBanner =
    interactive &&
    !derived &&
    !license.hasCarryoverEntry &&
    suggestedCarryover > 0 &&
    !license.isCompliant;
  // Explicit 0 entry while the engine still sees spare prior-period hours:
  // surface the excluded state (with re-apply) instead of silent nothing.
  const carryoverExcluded =
    !derived &&
    license.hasCarryoverEntry &&
    (license.carryoverHours ?? 0) === 0 &&
    suggestedCarryover > 0;

  async function handleCarryoverDecision(hours: number, action: 'apply' | 'exclude') {
    setCarryoverAction(action);
    setCarryoverApplyError(null);
    try {
      await licenseComplianceService.updateRequirement(license.licenseId, {
        totalHours: license.requiredTotalHours,
        ethicsHours: license.requiredEthicsHours,
        periodStart: license.periodStart,
        periodEnd: license.periodEnd,
        confirm: false,
        carryoverHours: hours,
      });
      setCarryoverAnnouncement(
        action === 'apply'
          ? `${hours} carry-over hours applied.`
          : 'Carry-over excluded.'
      );
      await onChanged?.();
    } catch (error) {
      setCarryoverApplyError(getApiErrorInfo(error).message);
    } finally {
      setCarryoverAction(null);
    }
  }
  // required hours of 0 means missing/unconfirmed data — don't render a full green bar
  const totalPct =
    license.requiredTotalHours > 0
      ? Math.min(100, (countableTotal / license.requiredTotalHours) * 100)
      : 0;
  // requiredTotalHours <= 0 means missing/unconfirmed data — a vacuous
  // "compliant" there is not an achievement (matches the celebration planner).
  const achieved = license.isCompliant && license.requiredTotalHours > 0;
  // States whose ethics rule is a specific attested course (e.g. TN/FL/TX) carry a
  // manual requirement row and an ethics hours target of 0. Showing the hours-based
  // Ethics tile as "Not Required" next to that row reads as a contradiction, so it
  // is hidden; states with no manual rows keep the explicit "Not Required" tile.
  const hideEthicsTile = hideHoursBasedEthics(license.requiredEthicsHours, license.subjectRequirements);

  return (
    // position: relative contains the celebration's confetti canvas to this card.
    // The id is the scroll target for derived (reciprocity) entries' "view
    // progress" link; scrollMarginTop keeps the header clear of the app bar.
    <Card
      id={`license-fulfillment-${license.licenseId}`}
      variant="outlined"
      sx={{ position: 'relative', scrollMarginTop: 80 }}
    >
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header: state identity + status chips | reporting period + provenance */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: '6px',
                bgcolor: 'brand.heroSurface',
                color: 'primary.contrastText',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.02em',
                flexShrink: 0,
              }}
            >
              {license.stateCode}
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {license.stateName || license.stateCode}
            </Typography>
            {license.isPrimary && <PrimaryLicenseChip />}
            {/* Single standing slot, matching the Compliance Report's precedence
                (Attention required > At risk > Compliant / In progress). At risk
                REPLACES In progress rather than sitting next to it — the two side
                by side read as "fine" and "not fine" at once. Derived entries
                suppress the pacing signals (they mirror the primary license). */}
            {!derived && attentionRequired ? (
              <>
                <Chip
                  label="Attention required"
                  size="small"
                  sx={{ bgcolor: 'status.attention.bg', color: 'status.attention.fg' }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'status.attention.dot' }} />
                  <Typography variant="caption" sx={{ color: 'status.attention.fg', fontWeight: 600 }}>
                    {periodEnded ? 'Period ended' : `${daysLeft} ${daysLeft === 1 ? 'day' : 'days'} left`}
                  </Typography>
                </Box>
              </>
            ) : !derived && atRisk ? (
              <Tooltip
                title="Projected to fall short of this period's CPE requirement at the current pace."
                arrow
              >
                <Chip
                  label="At risk"
                  size="small"
                  sx={{ bgcolor: 'status.atRisk.bg', color: 'status.atRisk.fg' }}
                />
              </Tooltip>
            ) : (
              <StatusChip compliant={license.isCompliant} pop={justAchieved && !reducedMotion} />
            )}
            {!derived && license.stateDataUpdatedSinceReview && (
              <Tooltip
                title="State requirement data changed after you last reviewed this license. Review and re-confirm it on the Licenses page."
                arrow
              >
                <Chip
                  label="State data updated"
                  size="small"
                  sx={{ bgcolor: 'status.modified.bg', color: 'status.modified.fg' }}
                />
              </Tooltip>
            )}
            {/* Fallback-window honesty: only while unconfirmed — once reviewed,
                the user's window is authoritative even if the anchor is absent. */}
            {license.status === 'unconfirmed' && license.missingPeriodAnchor && (
              <Tooltip
                title={
                  license.missingPeriodAnchor === 'birth_date'
                    ? interactive
                      ? 'This state calculates reporting periods from your birth date, which you haven\u2019t provided. Add it on your Profile to replace this calendar-based estimate with your actual period.'
                      : 'This state calculates reporting periods from the birth date, which this member hasn\u2019t provided. The dates shown are a calendar-based estimate.'
                    : interactive
                      ? 'This state calculates reporting periods from the license issue date, which this license doesn\u2019t have. Add it on the Licenses page to replace this calendar-based estimate with your actual period.'
                      : 'This state calculates reporting periods from the license issue date, which this member\u2019s license doesn\u2019t have. The dates shown are a calendar-based estimate.'
                }
                arrow
              >
                <Chip
                  label="Estimated period"
                  size="small"
                  sx={{ bgcolor: 'status.modified.bg', color: 'status.modified.fg' }}
                />
              </Tooltip>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Reporting period
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {formatUtcDate(license.periodStart)} – {formatUtcDate(license.periodEnd)}
            </Typography>
            {/* Exception-only provenance: confirmed defaults are the normal
                state, so they're silent here — a green "Defaults confirmed"
                chip reads as a second success competing with the standing
                chip. Unconfirmed / Custom / Not set up still show (actionable).
                Derived rows show reciprocity instead — provenance would be
                false there (values mirror the primary license). */}
            {derived ? (
              <Chip
                label="Home-state reciprocity"
                size="small"
                sx={{ bgcolor: 'status.enrolled.bg', color: 'status.enrolled.fg' }}
              />
            ) : license.status !== 'confirmed' ? (
              <ProvenanceBadge status={license.status} stateCode={license.stateCode} />
            ) : null}
          </Box>
        </Box>

        {/* Elected reciprocity whose gate failed at read time (e.g. the primary
            license was un-confirmed): the engine fell back to standard
            evaluation, so everything below is real — this just explains why. */}
        {license.reciprocityGateFailed && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {interactive
              ? 'Home-state reciprocity is elected for this license but can\u2019t currently be applied, usually because your primary license\u2019s requirements aren\u2019t set up. Standard tracking is shown until it\u2019s available again.'
              : 'Home-state reciprocity is elected for this license but can\u2019t currently be applied, usually because the member\u2019s primary license\u2019s requirements aren\u2019t set up. Standard tracking is shown instead.'}
          </Alert>
        )}

        {/* Discovery hint: eligible but not elected. The pain (a behind-schedule
            non-home-state license) is felt here on Reporting, but election lives
            on License Management — this bridges the two. Member view only; the
            admin drawer shouldn't nudge an action only the member can take. */}
        {interactive && !derived && license.reciprocityEligible && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {license.stateName || license.stateCode} accepts home-state reciprocity: meeting
            your {reciprocitySourceName || 'home state'} CPE requirements may satisfy this
            license.{' '}
            {/* color="inherit": same AA rationale as the carry-over banner link. */}
            <Link component={RouterLink} to="/app/licenses" color="inherit">
              Elect it in license settings
            </Link>
            .
          </Alert>
        )}

        {/* Carry-over suggestion: prior-period countable excess the state may
            let count toward this period. One click applies it; the entry then
            suppresses the banner and the hero caption takes over with the
            opposite action (Exclude, and after excluding, Apply again).
            Member view only — applying is the member's call. */}
        {/* Not an Alert on purpose: the blue info treatment (shared with the
            reciprocity hint above) reads as informational-only. This is a
            decision surface, so it gets the brand suggestion treatment — soft
            primary tint + a real outlined button. No live-region role here:
            controls don't belong inside one, and outcomes are announced via
            the persistent hidden status region below.
            Dark-step text/links measure ≥4.75:1 on the 8% tint (AA). */}
        {showCarryoverBanner && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1.5,
              bgcolor: 'tint.primarySoft',
              border: '1px solid',
              borderColor: 'tint.primaryStrong',
              borderRadius: '8px',
              px: 2,
              py: 1.5,
              mb: 2,
            }}
          >
            <Typography variant="body2" sx={{ flex: '1 1 280px', minWidth: 0 }}>
              {license.stateName || license.stateCode} may allow {suggestedCarryover} extra{' '}
              {suggestedCarryover === 1 ? 'hour' : 'hours'} from last period to carry over
              toward your {license.requiredTotalHours}-hour total
              {license.carryoverMaxHours != null ? ` (state max ${license.carryoverMaxHours})` : ''}.
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => void handleCarryoverDecision(suggestedCarryover, 'apply')}
              disabled={carryoverAction != null}
              startIcon={
                carryoverAction === 'apply' ? <CircularProgress size={14} color="inherit" /> : undefined
              }
              // White fill pops the action off the tinted surface.
              sx={{ whiteSpace: 'nowrap', flexShrink: 0, bgcolor: 'background.paper' }}
            >
              Apply {suggestedCarryover} hrs
            </Button>
          </Box>
        )}
        {/* Persistent polite live region: exists before any update, so the
            apply/exclude outcome is reliably announced after the banner or
            caption it came from re-renders away. Visually hidden. */}
        <Box
          role="status"
          sx={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
            whiteSpace: 'nowrap',
          }}
        >
          {carryoverAnnouncement}
        </Box>
        {carryoverApplyError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {carryoverApplyError}
          </Alert>
        )}

        {derived ? (
          <>
            {/* Derived summary: local hours are irrelevant by definition, so the
                hero bar and breakdowns are replaced with the mirroring notice. */}
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                Satisfied via {sourceLabel} compliance
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                {interactive
                  ? `This license accepts home-state reciprocity: meeting your ${sourceLabel} CPE requirements satisfies it, so its status mirrors your ${sourceLabel} license.`
                  : `This license accepts home-state reciprocity: its status mirrors the member's ${sourceLabel} license.`}
              </Typography>
              {license.reciprocitySourceLicenseId && (
                <Link
                  component="button"
                  type="button"
                  variant="body2"
                  onClick={() =>
                    document
                      .getElementById(`license-fulfillment-${license.reciprocitySourceLicenseId}`)
                      ?.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' })
                  }
                  sx={{ mt: 1 }}
                >
                  View {sourceLabel} progress
                </Link>
              )}
            </Box>
            {/* Ethics carve-out: this state still requires its own ethics even
                under reciprocity, evaluated within the primary's period. Hours-
                tracked states show the hours tile; states whose rule is a specific
                attested course (e.g. FL's board-approved ethics) show the manual
                check-off row instead — the same hide rule as the standard view. */}
            {license.ethicsCarveoutApplies && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    '& > *': { py: 2 },
                    '& > *:first-of-type': { pt: 0 },
                    '& > *:last-child': { pb: 0 },
                  }}
                >
                  {!hideEthicsTile && (
                    <RequirementTile
                      name="Ethics"
                      earned={license.earnedEthicsHours}
                      required={license.requiredEthicsHours}
                      done={license.earnedEthicsHours >= license.requiredEthicsHours}
                      detail={`${license.stateName || license.stateCode} requires its own ethics hours even under reciprocity, counted within the reporting period above.`}
                      justMet={subJustMet(ETHICS_SUB_ID)}
                    />
                  )}
                  {license.subjectRequirements
                    .filter((req) => req.targetKind === 'manual')
                    .map((req) => (
                      <ManualRequirementTile
                        key={req.id}
                        licenseId={license.licenseId}
                        requirement={req}
                        interactive={interactive}
                        onChanged={onChanged}
                        justMet={subJustMet(req.id)}
                      />
                    ))}
                </Box>
              </>
            )}
          </>
        ) : (
          <>
        {/* Hero: countable total hours (credit limits applied) */}
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1, flexWrap: 'wrap' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
            {countableTotal.toFixed(1)}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            / {license.requiredTotalHours} total hours
          </Typography>
          {cappedExcess > 0 && (
            <Typography variant="caption" sx={{ color: 'status.modified.fg' }}>
              {license.earnedTotalHours.toFixed(1)} hrs earned; {cappedExcess.toFixed(1)} over a
              credit limit and not counted
            </Typography>
          )}
          {/* Admin drawer: read-only signal that an unapplied suggestion exists,
              so an "At risk" member with spare prior-period hours isn't chased
              unnecessarily. The member view shows the actionable banner instead. */}
          {!interactive && !license.hasCarryoverEntry && suggestedCarryover > 0 && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {suggestedCarryover} carry-over hrs may be available from the previous reporting
              period (not yet applied)
            </Typography>
          )}
        </Box>
        {/* Carry-over state gets its own row so it doesn't blend into the hero
            baseline. The fact reads as a small tinted pill (same tint family as
            the suggestion banner, so Apply visually "collapses into" it); the
            explanation and the mirrored action stay as a caption tail. */}
        {appliedCarryover > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Typography
              component="span"
              variant="caption"
              sx={{
                bgcolor: 'tint.primarySoft',
                border: '1px solid',
                borderColor: 'tint.primaryStrong',
                color: 'text.primary',
                fontWeight: 600,
                borderRadius: '999px',
                px: 1,
                py: 0.25,
                whiteSpace: 'nowrap',
              }}
            >
              +{appliedCarryover} hrs carried over
            </Typography>
            {/* Names the hero figure verbatim (same toFixed(1) rendering) so
                the reader can match the two numbers and see the carry-over is
                already inside the total, not on top of it. */}
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              included in the {countableTotal.toFixed(1)} total
              {/* The undo lives where the state is announced. Member view
                  only — an admin can't change the member's carry-over. */}
              {interactive && (
                <>
                  {' · '}
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => void handleCarryoverDecision(0, 'exclude')}
                    disabled={carryoverAction != null}
                    sx={{
                      verticalAlign: 'baseline',
                      '&:disabled': { color: 'text.disabled', cursor: 'default' },
                    }}
                  >
                    {carryoverAction === 'exclude' ? 'Excluding…' : 'Exclude carry-over'}
                  </Link>
                </>
              )}
            </Typography>
          </Box>
        )}
        {/* Excluded state: the member said no while spare prior-period hours
            still exist. Named (not silent) so the earlier decision stays
            visible, with re-apply one click away — the mirror of Exclude.
            Neutral tokens: an excluded carry-over is a fact, not a nudge. */}
        {carryoverExcluded && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
            <Typography
              component="span"
              variant="caption"
              sx={{
                bgcolor: 'status.enrolled.bg',
                color: 'status.enrolled.fg',
                fontWeight: 600,
                borderRadius: '999px',
                px: 1,
                py: 0.25,
                whiteSpace: 'nowrap',
              }}
            >
              {suggestedCarryover} carry-over hrs excluded
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {interactive ? 'at your request' : 'by the member'}
              {interactive && (
                <>
                  {' · '}
                  <Link
                    component="button"
                    type="button"
                    variant="caption"
                    onClick={() => void handleCarryoverDecision(suggestedCarryover, 'apply')}
                    disabled={carryoverAction != null}
                    sx={{
                      verticalAlign: 'baseline',
                      '&:disabled': { color: 'text.disabled', cursor: 'default' },
                    }}
                  >
                    {carryoverAction === 'apply' ? 'Applying…' : `Apply ${suggestedCarryover} hrs`}
                  </Link>
                </>
              )}
            </Typography>
          </Box>
        )}
        {/* Single-hue rule: bars encode amount only (fill length); completion is
            carried by the check chip and the compliant line, never by a
            primary→success hue flip, which reads as two near-identical greens. */}
        <LinearProgress
          variant="determinate"
          value={totalPct}
          // Explicit track for the 3:1 non-text contrast minimum (see above).
          sx={{ height: 8, bgcolor: 'tint.primarySoft' }}
        />
        {justAchieved && (
          <ComplianceCelebration
            stateName={license.stateName || license.stateCode}
            periodEnd={license.periodEnd}
          />
        )}
        {/* Resting achieved state: the celebration only fires once, but the
            goal-met confirmation stays on every later visit. */}
        {achieved && !justAchieved && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.25 }}>
            <CheckCircleRoundedIcon sx={{ fontSize: 20, color: 'status.completed.fg' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'status.completed.fg' }}>
              Compliant through {formatUtcDate(license.periodEnd)}. Nothing more due this period.
            </Typography>
          </Box>
        )}

        {/* Subject breakdown: ethics + tracked subject requirements.
            Section eyebrows use full-strength ink + 700: at 0.75rem a
            text.secondary overline carries less weight than the row names
            below it, so the section boundaries disappear on scan.
            Two-tier separation: section boundaries get the full-width divider
            (13% ink) plus extra air above; rows inside a section separate with
            whitespace only (32px gap). A rule of any length or weight between
            rows competes with the section boundary, so rows get none. The
            asymmetric divider margin binds each header to its own content. */}
        <Divider sx={{ mt: 3, mb: 2 }} />
        <Typography
          variant="overline"
          sx={{ color: 'text.primary', fontWeight: 700, display: 'block', mb: 1.5, lineHeight: 1.5 }}
        >
          Subject breakdown
        </Typography>
        {/* Requirements are a checklist, read top to bottom. Row rhythm and
            the whisper separators live here so the tiles stay layout-agnostic. */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            '& > *': { py: 2 },
            '& > *:first-of-type': { pt: 0 },
            '& > *:last-child': { pb: 0 },
          }}
        >
          {!hideEthicsTile && (
            <RequirementTile
              name="Ethics"
              earned={license.earnedEthicsHours}
              required={license.requiredEthicsHours}
              done={license.earnedEthicsHours >= license.requiredEthicsHours}
              justMet={subJustMet(ETHICS_SUB_ID)}
            />
          )}
          {license.subjectRequirements.map((req) =>
            req.targetKind === 'manual' ? (
              <ManualRequirementTile
                key={req.id}
                licenseId={license.licenseId}
                requirement={req}
                interactive={interactive}
                onChanged={onChanged}
                justMet={subJustMet(req.id)}
              />
            ) : (
              <RequirementTile
                key={req.id}
                name={req.name}
                earned={req.earnedHours}
                required={req.requiredHours}
                done={isSubjectRequirementMet(req)}
                chip={
                  req.scope === 'per_year' && !req.isMet
                    ? { key: 'attention', label: 'Attention required' }
                    : undefined
                }
                detail={subjectDetail(req)}
                buckets={req.yearBuckets}
                justMet={subJustMet(req.id)}
              />
            )
          )}
        </Box>

        {/* Credit limits: delivery-method caps — usage meters, informational only.
            The framing caption states the section's one rule up front, and the
            neutral (grey) meters below keep cap usage visually distinct from the
            green progress-toward-goal bars in the subject breakdown. */}
        {creditLimits.length > 0 && (
          <>
            <Divider sx={{ mt: 3, mb: 2 }} />
            <Typography
              variant="overline"
              sx={{ color: 'text.primary', fontWeight: 700, display: 'block', lineHeight: 1.5 }}
            >
              Credit limits
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1.5 }}>
              Hours over a credit limit do not count toward totals.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                '& > *': { py: 2 },
                '& > *:first-of-type': { pt: 0 },
                '& > *:last-child': { pb: 0 },
              }}
            >
              {creditLimits.map((limit) => (
                <CreditLimitTile key={limit.id} limit={limit} />
              ))}
            </Box>
          </>
        )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
